import { ChangeDetectorRef, Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { WatchAwareComponent } from '../watch-aware.component';
import { View } from 'src/graphics/view';
import { Gfx } from 'src/graphics/gfx';
import { C, L } from 'src/sync/_api';
import { ForceViewSyncMessage } from '../messenger-service';
import { Account } from 'src/sync/account';
import { Room } from 'src/sync/room';
import { Util } from 'src/util/util';
import { ConfirmationDialog } from '../confirmation-dialog.component';
import { IKBEvent, Kb } from 'src/util/kb';
import { HumanizedKeyCode } from 'src/util/keycode';
import { Store } from 'src/sync/store';
import { Dice } from 'src/util/dice';
import { Graffiti } from 'src/graphics/graffiti';

export interface INinjaAction {
    title: string;
    hotkey?: IKBEvent;
    handler?: Function;
    mdIcon?: string;
    icon?: string;
    parent?: string;
    keywords?: string;
    children?: string[];
    section?: string;
}

@Component({
    selector: 'app-command-bar',
    templateUrl: './command-bar.component.html',
    styleUrls: ['./command-bar.component.scss'],
    standalone: false,
})
export class CommandBarComponent extends WatchAwareComponent {
    public data: INinjaAction[] = [];
    static instance?: CommandBarComponent;
    public query = "";
    public selected?: number;
    private lastMouseHoverPosition: { x?: number, y?: number } = { x: undefined, y: undefined };

    constructor(
        public dialog: MatDialog,
        public snackbar: MatSnackBar,
        override changeDetectorRef: ChangeDetectorRef,
    ) {
        super(changeDetectorRef);
        CommandBarComponent.instance = this;
    }

    public open() {
        const input = document.querySelector('#command') as HTMLInputElement;
        input?.focus();
    }

    public ngAfterViewInit() {
        // Register the commands
        this.data.push(...this.general);

        if (Store.fullAccess.value) {
            this.data.push(...this.mapping);
        }

        if (Store.isReferee.value) {
            this.data.push(...this.referee);
        }

        Kb.on(
            {
                code: 'KeyK',
                ctrl: true,
                preventDefault: true,
            },
            () => this.open(),
        )

        this.data.filter((c) => c.hotkey && c.handler).forEach((c) => {
            Kb.on(
                c.hotkey!,
                () => c.handler!()
            );
        });
    }

    public general: INinjaAction[] = [
        {
            title: 'Align Viewport to Revealed',
            hotkey: {
                code: 'KeyA',
                ctrl: true,
                preventDefault: true
            },
            handler: () => {
                this.snackbar.open(`Aligned your view to the revealed area.`, undefined, { duration: 1500 });
                View.fitExtents();
            }
        },
    ];

    public mapping: INinjaAction[] = [
        {
            title: 'Preview Fog',
            keywords: 'reveal hidden war fow',
            hotkey: {
                code: 'KeyP',
            },
            handler: () => {
                const target = Gfx.containers.layers[L.Fog].alpha == 1 ? Gfx.layers[L.Fog].cfg.alpha : 1;
                Gfx.containers.layers[L.Fog].alpha = target;
            }
        },
        {
            title: 'Force User View Sync',
            keywords: 'synchronize force look',
            hotkey: {
                code: 'KeyF',
                ctrl: true,
                preventDefault: true
            },
            handler: () => {
                const scale = View.viewport.scale.x;
                const position = { x: View.viewport.center.x, y: View.viewport.center.y };

                const message = new ForceViewSyncMessage({ position, scale }, Account.id)

                Room.sendStateless(message);

                Room.awareness.setLocalStateField('sync-view', { scale, position });
                Room.awareness.setLocalStateField('sync-view', undefined);
            }
        },
    ];

    public referee: INinjaAction[] = [
        {
            title: 'Save Scene',
            keywords: 'room download',
            hotkey: {
                code: 'KeyS',
                ctrl: true,
                preventDefault: true
            },
            handler: () => {
                const state = Room.document.getStateAsUpdate();
                Util.saveFile('room.uvt', state);
            }
        },
        {
            title: 'Load Scene',
            keywords: 'room upload',
            hotkey: {
                code: 'KeyL',
                ctrl: true,
                preventDefault: true
            },
            handler: () => {
                const input = document.querySelector('#upload') as HTMLInputElement;
                input.click();
                input.value = '';
            }
        },
        {
            title: 'Toggle User Freedom',
            keywords: 'collaborate lock free mapping',
            handler: () => {
                const target = !Room.settings.get().freedom;
                ConfirmationDialog.open(this.dialog,
                    target ? 'Grant Freedom' : 'Lockdown Users',
                    target ? 'Allow user to edit all layers and maps.' : 'Prevents users from editing layers or maps.',
                    () => Room.settings.modify(x => x.freedom = target),
                    false,
                )
            }
        },
        {
            title: 'Clear Chat History',
            keywords: 'clear remove trash message log',
            handler: () => ConfirmationDialog.open(this.dialog,
                'Clear Chat History',
                'This will permanently delete all chat messages.',
                () => Room.chat.delete(0, Room.chat.getLength()),
                false,
            )
        },
        {
            title: 'Clear All Layers',
            keywords: 'delete remove trash map',
            handler: () => ConfirmationDialog.open(this.dialog,
                'Clear All Layers',
                'This removes all layer data and restores fog.',
                () => {
                    Graffiti.clear();
                    Room.layerPolygons.forEach(x => x.delete(0, x.length));
                    Room.initFog(Gfx.layers[L.Fog].data.fillColor);
                },
                false,
            )
        },
        {
            title: 'Clear All Tokens',
            keywords: 'delete remove trash',
            handler: () => ConfirmationDialog.open(this.dialog,
                'Clear All Tokens',
                'This will remove all placed tokens.',
                () => {
                    Room.tokens.clear();
                },
                false,
            )
        },
        {
            title: 'Delete All Maps',
            keywords: 'clear remove trash',
            handler: () => ConfirmationDialog.open(this.dialog,
                'Delete All Maps',
                'This will delete all map uploads.',
                () => {
                    Room.document.transact(() => {
                        Room.imageAssets.clear();
                        Room.images.clear();
                    });
                },
                false,
            )
        },
        {
            title: 'Delete Everything',
            keywords: 'clear remove trash',
            handler: () => ConfirmationDialog.open(this.dialog,
                'Just Fuck My Shit Up Fam',
                'Delete all tokens, maps, and layers, and remove all other referees.',
                () => {
                    Room.document.transact(() => {
                        Room.imageAssets.clear();
                        Room.images.clear();
                        Room.tokenAssets.clear();
                        Room.tokens.clear();
                        Room.layerPolygons.forEach(x => x.delete(0, x.length));
                        Room.layers.clear();
                        Room.chat.delete(0, Room.chat.getLength());
                        Graffiti.clear();
                        Room.initFog(Gfx.layers[L.Fog].data.fillColor);
                    });
                },
                false,
            )
        }
    ];

    public uploadScene(event: any) {
        const blob = event.currentTarget.files[0] as Blob;
        if (blob == null) return;

        blob.arrayBuffer().then(bytes => {
            const data = new Uint8Array(bytes);
            Room.loadScene(data);
        });
    }

    public get filteredCommands() {
        const q = this.query.trim().toLowerCase();
        if (!q) return this.data;

        return this.data.filter(command =>
            command.title.toLowerCase().includes(q) ||
            command.keywords?.toLowerCase().includes(q)
        );
    }

    public handleEnter(e?: Event) {
        if (e) { e.preventDefault(); e.stopPropagation(); }

        const roll = Dice.roll(this.query);
        if (roll != undefined) {
            Room.shout(this.query, C.All);
            this.blurCommand();
        }

        this.handleSelected();
        this.blurCommand();
    }

    private handleSelected() {
        if (this.selected === undefined) return;
        this.filteredCommands[this.selected]?.handler?.()
    }

    public selectionUp(e?: Event) {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (this.selected === undefined || this.selected === 0) { this.selected = this.filteredCommands.length - 1 }
        else { this.selected -= 1 };

        this.selected = Math.max(0, Math.min(this.filteredCommands.length - 1, this.selected));

        requestAnimationFrame(() => {
            const selectedElement = document.querySelector('.command.selected');
            selectedElement?.scrollIntoView();
        });
    }

    public mouseOver(index: number, e?: Event) {
        const mousePosition = { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
        if (mousePosition === this.lastMouseHoverPosition) return;
        this.lastMouseHoverPosition = mousePosition;
        this.selected = index;
    }

    public selectionDown(e?: Event) {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (this.selected == undefined || this.selected === this.filteredCommands.length - 1) { this.selected = 0 }
        else { this.selected += 1 };

        this.selected = Math.max(0, Math.min(this.filteredCommands.length - 1, this.selected));

        requestAnimationFrame(() => {
            const selectedElement = document.querySelector('.command.selected');
            selectedElement?.scrollIntoView();
        });
    }

    public constructHotkeyString(hotkey: IKBEvent) {
        return `${hotkey?.ctrl ? 'Ctrl+' : ''}${hotkey?.shift ? 'Shift+' : ''}${hotkey?.code ? HumanizedKeyCode[hotkey?.code] : ''}`;
    }

    public blurCommand(e?: Event) {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        const input = document.querySelector('#command') as HTMLInputElement;
        this.query = '';
        input?.blur();
        this.selected = undefined;
    }
}
